/**
 * Calendario mensual que solo deja elegir días en que el profesional atiende.
 *
 * Reemplaza al `<input type="date">` nativo, que no puede deshabilitar días de
 * la semana (solo admite min/max): dejaba elegir cualquier fecha y recién
 * después avisaba "no atiende los viernes". Ahora el día imposible ni siquiera
 * es pulsable, y se ve de un vistazo cuáles sí.
 *
 * Deshabilita: días de la semana sin regla activa, días bloqueados
 * (vacaciones/permisos) y días pasados.
 */
import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { AgendaBloqueo, AgendaRegla } from "../api/agenda";

const DIAS_CABECERA = ["L", "M", "M", "J", "V", "S", "D"];
const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

/** "YYYY-MM-DD" sin pasar por Date, para no arrastrar zonas horarias. */
function ymd(anio: number, mes: number, dia: number): string {
  return `${anio}-${String(mes + 1).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
}

/** 0=Lunes … 6=Domingo, igual que `dia_semana` en el backend. */
function diaSemanaLunes(anio: number, mes: number, dia: number): number {
  return (new Date(anio, mes, dia).getDay() + 6) % 7;
}

export interface Disponibilidad {
  reglas: AgendaRegla[];
  bloqueos: AgendaBloqueo[];
  /** "YYYY-MM-DD" de hoy en la zona del policlínico. */
  hoy: string;
}

/** Días de la semana (0=Lun) en que el profesional tiene alguna regla activa. */
export function diasQueAtiende(reglas: AgendaRegla[]): Set<number> {
  return new Set(reglas.filter((r) => r.activo).map((r) => r.dia_semana));
}

/**
 * Solo cuenta el bloqueo de DÍA COMPLETO (sin horas). Uno parcial deja horas
 * libres, así que apagar el día entero sería mentir: el usuario vería tachado
 * un día en el que sí puede agendar por la tarde.
 */
function estaBloqueado(fecha: string, bloqueos: AgendaBloqueo[]): boolean {
  return bloqueos.some(
    (b) =>
      b.hora_inicio == null &&
      fecha >= b.fecha_inicio &&
      fecha <= b.fecha_fin,
  );
}

/**
 * Primer día a partir de `desde` (incluido) en que el profesional atiende.
 * Devuelve null si no atiende ningún día — no tiene sentido buscar infinito.
 */
export function proximoDiaDisponible(
  desde: string,
  { reglas, bloqueos }: Omit<Disponibilidad, "hoy">,
): string | null {
  const dias = diasQueAtiende(reglas);
  if (dias.size === 0) return null;
  const [a, m, d] = desde.split("-").map(Number);
  const cursor = new Date(a, m - 1, d);
  // Dos meses de margen: cubre cualquier combinación de días + bloqueos largos.
  for (let i = 0; i < 62; i++) {
    const f = ymd(cursor.getFullYear(), cursor.getMonth(), cursor.getDate());
    const dow = (cursor.getDay() + 6) % 7;
    if (dias.has(dow) && !estaBloqueado(f, bloqueos)) return f;
    cursor.setDate(cursor.getDate() + 1);
  }
  return null;
}

/**
 * Lo que ya ocupa un día, para pintarlo en el calendario.
 *
 * Se distingue lo pagado de lo que está por cobrar porque no son lo mismo para
 * quien mira: una reserva sin pagar todavía puede caerse, y el paciente puede
 * pagarla por adelantado o el día antes de la cita.
 */
export interface MarcaDia {
  pagadas: number;
  porCobrar: number;
  /**
   * Número de sesión del tratamiento que se está repartiendo, si cae ese día.
   * Se muestra como "S2" y no como un número suelto: un "2" a secas se leía
   * como "dos citas" o como "sesión 2" según quien mirara.
   */
  sesion?: number | null;
}

/** Fondo y borde de un día según lo que tenga encima. */
function celdaEstilo(
  c: { fecha: string; habil: boolean; marca: MarcaDia | null },
  seleccionado: string,
): string {
  if (c.fecha === seleccionado) {
    return "border-transparent bg-[color:var(--color-link)] font-semibold text-white";
  }
  if (!c.habil) return "border-transparent cursor-not-allowed text-slate-300 line-through";
  // Un día con sesión de ESTE tratamiento se reconoce antes que nada: es lo
  // que se está repartiendo, y tiene que saltar por encima del resto.
  if (c.marca?.sesion) {
    return "border-[color:var(--color-link)] bg-[color-mix(in_srgb,var(--color-link)_10%,white)] font-semibold text-slate-900";
  }
  // Ocupado sin pagar frente a ocupado y cobrado: el color lo dice antes de
  // leer nada. Ámbar es "esto todavía puede caerse".
  if (c.marca && c.marca.porCobrar > 0) {
    return "border-amber-300 bg-amber-50 text-slate-900 hover:bg-amber-100";
  }
  if (c.marca && c.marca.pagadas > 0) {
    return "border-emerald-300 bg-emerald-50 text-slate-900 hover:bg-emerald-100";
  }
  return "border-transparent text-slate-800 hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]";
}

/** Lo que va debajo del número: la sesión del plan o los puntos de carga. */
function Marca({ marca, activo }: { marca: MarcaDia; activo: boolean }) {
  if (marca.sesion) {
    return (
      <span
        className={`rounded px-1 text-[10px] font-bold ${
          activo ? "bg-white/25 text-white" : "bg-[color:var(--color-link)] text-white"
        }`}
      >
        S{marca.sesion}
      </span>
    );
  }
  const total = marca.pagadas + marca.porCobrar;
  if (total === 0) return null;
  // Un punto por cita hasta tres; a partir de ahí un "+", porque contar bolitas
  // deja de servir y lo único que importa es "ese día está cargado".
  const puntos = [
    ...Array.from({ length: Math.min(marca.pagadas, 3) }, () => "pagada"),
    ...Array.from({ length: Math.min(marca.porCobrar, 3) }, () => "porCobrar"),
  ].slice(0, 3);
  return (
    <span className="flex items-center gap-0.5">
      {puntos.map((tipo, i) => (
        <span
          key={i}
          className={`h-1.5 w-1.5 rounded-full ${
            activo
              ? "bg-white/80"
              : tipo === "pagada"
                ? "bg-emerald-500"
                : "bg-amber-500"
          }`}
        />
      ))}
      {total > 3 && (
        <span
          className={`text-[9px] font-semibold ${activo ? "text-white/80" : "text-slate-500"}`}
        >
          +
        </span>
      )}
    </span>
  );
}

interface Props {
  /** "YYYY-MM-DD" seleccionado. */
  value: string;
  onChange: (fecha: string) => void;
  disponibilidad: Disponibilidad;
  /** Sin profesional elegido no hay nada que marcar. */
  deshabilitado?: boolean;
  /**
   * Qué hay ya en cada día: { "2026-09-03": { pagadas: 0, porCobrar: 1 } }.
   *
   * Opcional a propósito. Quien agenda una cita suelta solo necesita saber qué
   * días atiende el profesional; quien reparte varias sesiones necesita además
   * ver dónde ya hay carga, para no amontonarlas todas en el mismo día.
   */
  marcasPorDia?: Record<string, MarcaDia>;
  /** Se avisa al padre qué mes se está mirando, para que traiga sus citas. */
  onMesVisible?: (anio: number, mes: number) => void;
}

export function CalendarioDisponibilidad({
  value,
  onChange,
  disponibilidad,
  deshabilitado = false,
  marcasPorDia,
  onMesVisible,
}: Props) {
  const { reglas, bloqueos, hoy } = disponibilidad;
  const [anio, mes] = value.split("-").map(Number);
  const [visible, setVisible] = useState({ anio: anio, mes: mes - 1 });

  const atiende = useMemo(() => diasQueAtiende(reglas), [reglas]);

  const celdas = useMemo(() => {
    const primero = new Date(visible.anio, visible.mes, 1);
    const total = new Date(visible.anio, visible.mes + 1, 0).getDate();
    const hueco = (primero.getDay() + 6) % 7; // cuántos blancos antes del día 1
    const items: ({
      dia: number;
      fecha: string;
      habil: boolean;
      motivo: string;
      marca: MarcaDia | null;
    } | null)[] = Array.from({ length: hueco }, () => null);

    for (let dia = 1; dia <= total; dia++) {
      const fecha = ymd(visible.anio, visible.mes, dia);
      const dow = diaSemanaLunes(visible.anio, visible.mes, dia);
      let habil = true;
      let motivo = "";
      if (fecha < hoy) {
        habil = false;
        motivo = "Fecha pasada";
      } else if (!atiende.has(dow)) {
        habil = false;
        motivo = "No atiende este día";
      } else if (estaBloqueado(fecha, bloqueos)) {
        habil = false;
        motivo = "Bloqueado (vacaciones o permiso)";
      }
      items.push({ dia, fecha, habil, motivo, marca: marcasPorDia?.[fecha] ?? null });
    }
    return items;
  }, [visible, atiende, bloqueos, hoy, marcasPorDia]);

  function mover(delta: number) {
    const d = new Date(visible.anio, visible.mes + delta, 1);
    setVisible({ anio: d.getFullYear(), mes: d.getMonth() });
    onMesVisible?.(d.getFullYear(), d.getMonth());
  }

  const sinReglas = reglas.filter((r) => r.activo).length === 0;

  return (
    <div
      className={`rounded-lg border border-slate-200 p-3 ${deshabilitado ? "opacity-50" : ""}`}
      aria-disabled={deshabilitado}
    >
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => mover(-1)}
          disabled={deshabilitado}
          className="rounded p-1 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)] disabled:opacity-40"
          aria-label="Mes anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-medium capitalize text-slate-800">
          {MESES[visible.mes]} {visible.anio}
        </span>
        <button
          type="button"
          onClick={() => mover(1)}
          disabled={deshabilitado}
          className="rounded p-1 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)] disabled:opacity-40"
          aria-label="Mes siguiente"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {DIAS_CABECERA.map((d, i) => (
          <div key={i} className="pb-1 text-[11px] font-medium text-slate-400">
            {d}
          </div>
        ))}

        {celdas.map((c, i) =>
          c === null ? (
            <div key={`v${i}`} />
          ) : (
            <button
              key={c.fecha}
              type="button"
              disabled={deshabilitado || !c.habil}
              onClick={() => onChange(c.fecha)}
              title={c.habil ? undefined : c.motivo}
              aria-label={`${c.dia}${c.habil ? "" : ` — ${c.motivo}`}`}
              aria-current={c.fecha === value ? "date" : undefined}
              // La celda crece solo donde hay marcas que mostrar: sin esto,
              // las pantallas que no las piden cambiarían de alto sin motivo.
              className={`flex flex-col items-center justify-center gap-0.5 rounded border text-sm leading-none transition focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)] ${
                marcasPorDia ? "h-12" : "h-8 border-transparent"
              } ${celdaEstilo(c, value)}`}
            >
              <span>{c.dia}</span>
              {c.habil && c.marca && <Marca marca={c.marca} activo={c.fecha === value} />}
            </button>
          ),
        )}
      </div>

      <p className="mt-2 text-[11px] text-slate-500">
        {sinReglas
          ? "Este profesional no tiene horarios configurados: no hay días disponibles."
          : "Los días tachados no están disponibles: no atiende, está bloqueado o ya pasó."}
      </p>

      {/* Ningún símbolo sin explicar: un color o un número sueltos se
          interpretan mal, y aquí lo que se interpreta mal es una cita. */}
      {marcasPorDia && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 pt-1.5 text-[11px] text-slate-600">
          <span className="inline-flex items-center gap-1">
            <span className="rounded bg-[color:var(--color-link)] px-1 text-[10px] font-bold text-white">
              S1
            </span>
            sesión de este tratamiento
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            cita por cobrar
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            cita pagada
          </span>
        </div>
      )}
    </div>
  );
}
